import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MegaphoneIcon } from 'lucide-react';
import {
  Button,
  ChoiceList,
  ConfirmDialog,
  DataTable,
  DatePicker,
  Drawer,
  EmptyState,
  ErrorNotice,
  Field,
  MultiCombobox,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  PromoBanner,
  SearchInput,
  SegmentedControl,
  SelectInput,
  SkeletonTable,
  TextArea,
  TextInput,
  type DataTableHeader,
  type DataTableSort,
  type PillTone,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { readableDay } from '@/lib/dev-tracker';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage } from '@/lib/use-list-query-state';
import {
  BANNER_AUDIENCE,
  BANNER_KIND,
  BANNER_KIND_OPTIONS,
  BANNER_LIMITS,
  BANNER_STATUS,
  BANNER_STATUS_OPTIONS,
  CTA_LINK_HINT,
  MAX_LIVE_BANNERS_PER_PORTAL,
  createPromoBanner,
  draftOf,
  draftProblems,
  emptyDraft,
  inputOf,
  isSafeCtaHref,
  kindLabel,
  listClientOrganizations,
  listPromoBanners,
  removePromoBanner,
  statusLabel,
  updatePromoBanner,
  type BannerAudience,
  type BannerDraft,
  type BannerStatus,
  type PromoBanner as PromoBannerRecord,
} from '@/lib/promo-banners';

const PAGE_SIZE = 25;
const QUERY_KEY = ['promo-banners'] as const;
/** How many organization names a row shows before "and N more". */
const AUDIENCE_NAMES_SHOWN = 2;

const STATUS_TONES: Readonly<Record<BannerStatus, PillTone>> = {
  [BANNER_STATUS.live]: 'success',
  [BANNER_STATUS.scheduled]: 'info',
  [BANNER_STATUS.ended]: 'neutral',
};

/** The filter above the table. */
const VIEW = { all: 'all', ...BANNER_STATUS } as const;
type View = (typeof VIEW)[keyof typeof VIEW];

const SORT_FIELDS = { banner: 'headline', audience: 'audienceLabel', status: 'statusOrder', starts: 'startsOn', ends: 'endsOn' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const HEADERS: readonly DataTableHeader[] = [
  { label: 'Banner', sortKey: SORT_FIELDS.banner },
  { label: 'Shows to', sortKey: SORT_FIELDS.audience },
  { label: 'Status', sortKey: SORT_FIELDS.status },
  { label: 'Starts', sortKey: SORT_FIELDS.starts },
  { label: 'Ends', sortKey: SORT_FIELDS.ends },
];
/** Latest start first, so what is coming up and what is live lead. */
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.starts, direction: 'desc' };

/** Who a banner reaches, in a table cell's words. */
function audienceLabel(banner: PromoBannerRecord): string {
  if (banner.audience === BANNER_AUDIENCE.allPortals) return 'All client portals';
  const names = banner.organizations.map((organization) => organization.name);
  if (names.length <= AUDIENCE_NAMES_SHOWN) return names.join(', ');
  return `${names.slice(0, AUDIENCE_NAMES_SHOWN).join(', ')} and ${names.length - AUDIENCE_NAMES_SHOWN} more`;
}

/** More banners could share a portal than a portal shows at once. */
function isCrowded(banner: PromoBannerRecord): boolean {
  return banner.status !== BANNER_STATUS.ended && banner.overlapping >= MAX_LIVE_BANNERS_PER_PORTAL;
}

/**
 * Overview banners (Roy's portal list, item 10; 2026-09-29): where BCE adds,
 * schedules and removes the banners that rotate at the top of a client
 * portal's Overview, for every client portal or chosen organizations.
 *
 * @returns The page.
 */
export function PromoBannersPage(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<View>(VIEW.all);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const { page, setPage } = useUrlPage();

  const banners = useQuery({ queryKey: QUERY_KEY, queryFn: ({ signal }) => listPromoBanners(signal) });

  const all = banners.data ?? [];
  const countOf = (status: BannerStatus): number => all.filter((banner) => banner.status === status).length;
  const needle = search.trim().toLowerCase();
  const rows = sortRows(
    all
      .filter((banner) => view === VIEW.all || banner.status === view)
      .filter(
        (banner) =>
          needle === '' ||
          `${banner.headline} ${banner.eyebrow} ${banner.body} ${kindLabel(banner.kind)} ${audienceLabel(banner)}`.toLowerCase().includes(needle),
      )
      .map((banner) => ({
        ...banner,
        audienceLabel: audienceLabel(banner),
        statusOrder: BANNER_STATUS_OPTIONS.findIndex((option) => option.key === banner.status),
        startsOn: banner.startsOn ?? '',
        endsOn: banner.endsOn ?? '',
      })),
    sort ?? DEFAULT_SORT,
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const open = all.find((banner) => banner.id === openId) ?? null;
  const firstCrowdedId = shown.find(isCrowded)?.id;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="CLIENT PORTALS"
        title="Overview banners"
        subtitle={`Banners rotate at the top of a client portal's Overview. Each portal shows up to ${MAX_LIVE_BANNERS_PER_PORTAL} at a time, so keep them few and aimed at the right clients.`}
        action={<Button guide="banners-add" onClick={() => setAdding(true)}>Add banner</Button>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search banners"
          placeholder="A headline, a word from the banner, an organization"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-full max-w-sm"
        />
        <SegmentedControl
          guide="banners-filter"
          label="Which banners to show"
          options={[
            { key: VIEW.all, label: `All (${all.length})` },
            ...BANNER_STATUS_OPTIONS.map((option) => ({ key: option.key, label: `${option.label} (${countOf(option.key)})` })),
          ]}
          value={view}
          onChange={(next) => {
            setView(next);
            setPage(1);
          }}
        />
      </div>

      {banners.isError ? (
        <ErrorNotice title="Couldn’t load the banners" message={errorMessage(banners.error)} />
      ) : banners.isPending ? (
        <SkeletonTable rows={5} columns={['w-[40%]', 'w-[20%]', 'w-[12%]', 'w-[12%]', 'w-[12%]']} label="Loading banners…" />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<MegaphoneIcon aria-hidden className="size-6" />}
            title={all.length === 0 ? 'No banners yet' : 'No banners match'}
            message={
              all.length === 0
                ? 'A banner puts a timely offer at the top of a client’s Overview: a season to book ahead for, a large event to plan, credits about to expire.'
                : 'Try a different search or show all banners.'
            }
            {...(all.length === 0 ? { action: <Button onClick={() => setAdding(true)}>Add banner</Button> } : {})}
          />
        </Panel>
      ) : (
        <>
          <DataTable guide="banners-table" headers={HEADERS} {...(sort === undefined ? {} : { sort })} onSort={onSort}>
            {shown.map((banner) => (
              <tr key={banner.id} className="border-t border-portal-line align-middle">
                <td className="p-3 text-portal-small">
                  <button
                    type="button"
                    // The first crowded banner is where the tour shows the warning.
                    data-guide={banner.id === firstCrowdedId ? 'banners-open-crowded' : undefined}
                    onClick={() => setOpenId(banner.id)}
                    className="text-left font-bold text-brand underline-offset-2 hover:underline"
                  >
                    {banner.headline}
                  </button>
                  <span className="block text-portal-micro text-portal-muted">{kindLabel(banner.kind)}</span>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">{banner.audienceLabel}</td>
                <td className="p-3 text-portal-small">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <Pill tone={STATUS_TONES[banner.status]}>{statusLabel(banner.status)}</Pill>
                    {isCrowded(banner) ? <Pill tone="warning">Crowded</Pill> : null}
                  </span>
                </td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDay(banner.startsOn || null) || 'Any time'}</td>
                <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{readableDay(banner.endsOn || null) || 'No end date'}</td>
              </tr>
            ))}
          </DataTable>
          {totalPages > 1 ? (
            <div className="mt-4">
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          ) : null}
        </>
      )}

      <BannerDrawer banner={open} onClose={() => setOpenId(null)} />
      <AddBannerDrawer
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={(id) => {
          setAdding(false);
          setOpenId(id);
        }}
      />
    </section>
  );
}

/** A field's hint, or what is wrong with it once someone has typed. */
function hintFor(problem: string | undefined, fallback: string | undefined, touched: boolean): string | undefined {
  return touched && problem !== undefined ? problem : fallback;
}

/**
 * The banner as a portal draws it, above the form, updating as it is typed.
 *
 * @param props - The draft.
 * @returns The preview.
 */
function BannerPreview({ draft }: { readonly draft: BannerDraft }): React.ReactElement {
  const href = draft.ctaHref.trim();
  return (
    <section data-guide="banner-preview" aria-label="Preview" className="space-y-2">
      <p className="text-portal-tiny font-bold">Preview</p>
      <PromoBanner
        preview
        eyebrow={draft.eyebrow.trim() || null}
        headline={draft.headline.trim() || 'Your headline'}
        body={draft.body.trim() || null}
        ctaLabel={draft.ctaLabel.trim() || null}
        ctaHref={isSafeCtaHref(href) ? href : null}
      />
      <p className="text-portal-micro text-portal-muted">Each client sees it in their own portal colors.</p>
    </section>
  );
}

/**
 * The fields a banner is made of, shared by adding and editing.
 *
 * @param props - The draft, its setter, and whether problems should show yet.
 * @returns The fields.
 */
function BannerFields({
  idPrefix,
  draft,
  setDraft,
  touched,
}: {
  readonly idPrefix: string;
  readonly draft: BannerDraft;
  readonly setDraft: (next: BannerDraft) => void;
  readonly touched: boolean;
}): React.ReactElement {
  const organizations = useQuery({
    queryKey: ['promo-banner-organizations'],
    queryFn: ({ signal }) => listClientOrganizations(signal),
    staleTime: 5 * 60_000,
  });
  const problems = draftProblems(draft);
  const set = <K extends keyof BannerDraft>(key: K, value: BannerDraft[K]): void => setDraft({ ...draft, [key]: value });

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field guide="banner-kind" label="Kind" htmlFor={`${idPrefix}-kind`} required>
          <SelectInput
            id={`${idPrefix}-kind`}
            value={draft.kind}
            onChange={(event) => set('kind', BANNER_KIND_OPTIONS.find((option) => option.key === event.target.value)?.key ?? BANNER_KIND.custom)}
          >
            {BANNER_KIND_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field guide="banner-eyebrow" label="Eyebrow" htmlFor={`${idPrefix}-eyebrow`} hint="A short line above the headline, such as PLAN AHEAD.">
          <TextInput
            id={`${idPrefix}-eyebrow`}
            maxLength={BANNER_LIMITS.eyebrow}
            value={draft.eyebrow}
            onChange={(event) => set('eyebrow', event.target.value)}
          />
        </Field>
      </div>
      <Field guide="banner-headline" label="Headline" htmlFor={`${idPrefix}-headline`} required hint={hintFor(problems.headline, undefined, touched)}>
        <TextInput
          id={`${idPrefix}-headline`}
          maxLength={BANNER_LIMITS.headline}
          value={draft.headline}
          onChange={(event) => set('headline', event.target.value)}
        />
      </Field>
      <Field guide="banner-body" label="Body" htmlFor={`${idPrefix}-body`} hint="One or two sentences on why it matters now.">
        <TextArea
          id={`${idPrefix}-body`}
          rows={3}
          maxLength={BANNER_LIMITS.body}
          value={draft.body}
          onChange={(event) => set('body', event.target.value)}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field guide="banner-cta-label" label="Button label" htmlFor={`${idPrefix}-cta-label`} hint={hintFor(problems.ctaLabel, 'Leave both blank for no button.', touched)}>
          <TextInput
            id={`${idPrefix}-cta-label`}
            maxLength={BANNER_LIMITS.ctaLabel}
            value={draft.ctaLabel}
            onChange={(event) => set('ctaLabel', event.target.value)}
          />
        </Field>
        <Field
          guide="banner-cta-link"
          label="Button link"
          htmlFor={`${idPrefix}-cta-href`}
          // A bad link is flagged as soon as it is typed: it is the one field that can be unsafe.
          hint={hintFor(problems.ctaHref, CTA_LINK_HINT, touched || (problems.ctaHref !== undefined && draft.ctaHref.trim() !== ''))}
        >
          <TextInput
            id={`${idPrefix}-cta-href`}
            maxLength={BANNER_LIMITS.ctaHref}
            placeholder="/programs"
            value={draft.ctaHref}
            onChange={(event) => set('ctaHref', event.target.value)}
          />
        </Field>
      </div>

      <Field guide="banner-audience" label="Shows to" htmlFor={`${idPrefix}-organizations`} required hint={hintFor(problems.organizationIds, undefined, touched)}>
        <ChoiceList<BannerAudience>
          label="Who sees this banner"
          value={draft.audience}
          onChange={(audience) => set('audience', audience)}
          options={[
            { key: BANNER_AUDIENCE.allPortals, label: 'All client portals' },
            { key: BANNER_AUDIENCE.organizations, label: 'Specific organizations' },
          ]}
          renderSelected={(key) =>
            key === BANNER_AUDIENCE.organizations ? (
              organizations.isError ? (
                <ErrorNotice inline title="Organizations didn’t load" message={errorMessage(organizations.error)} />
              ) : (
                <MultiCombobox
                  id={`${idPrefix}-organizations`}
                  placeholder={organizations.isPending ? 'Loading organizations…' : 'Type to find an organization'}
                  disabled={organizations.isPending}
                  options={(organizations.data ?? []).map((organization) => ({ value: organization.id, label: organization.name }))}
                  values={draft.organizationIds}
                  onChange={(values) => set('organizationIds', values)}
                />
              )
            ) : null
          }
        />
      </Field>

      <div data-guide="banner-dates" className="grid gap-4 sm:grid-cols-2">
        <Field label="Starts" htmlFor={`${idPrefix}-starts`} required hint={hintFor(problems.startsOn, 'Shows from the start of this day, Eastern time.', touched)}>
          <DatePicker id={`${idPrefix}-starts`} value={draft.startsOn} onChange={(value) => set('startsOn', value)} />
        </Field>
        <Field guide="banner-ends" label="Ends" htmlFor={`${idPrefix}-ends`} required hint={hintFor(problems.endsOn, 'Shows through the end of this day.', touched)}>
          <DatePicker
            id={`${idPrefix}-ends`}
            value={draft.endsOn}
            {...(draft.startsOn === '' ? {} : { min: draft.startsOn })}
            onChange={(value) => set('endsOn', value)}
          />
        </Field>
      </div>
    </div>
  );
}

/**
 * Adding a banner: the preview on top, the fields under it.
 *
 * @param props - Whether it is open, and what to do when done.
 * @returns The side panel.
 */
function AddBannerDrawer({
  open,
  onClose,
  onAdded,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAdded: (id: string) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BannerDraft>(emptyDraft);
  const [touched, setTouched] = useState(false);
  const canSave = Object.keys(draftProblems(draft)).length === 0;

  const add = useMutation({
    mutationFn: () => createPromoBanner(inputOf(draft)),
    onSuccess: async (banner) => {
      setDraft(emptyDraft());
      setTouched(false);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      onAdded(banner.id);
    },
  });

  return (
    <Drawer
      open={open}
      size="wide"
      title="Add a banner"
      subtitle="It shows at the top of the Overview for the portals you choose, between the dates you set."
      onClose={() => {
        if (!add.isPending) onClose();
      }}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            guide="banner-add-submit"
            disabled={add.isPending}
            onClick={() => {
              setTouched(true);
              if (canSave) add.mutate();
            }}
          >
            {add.isPending ? 'Adding…' : 'Add banner'}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <BannerPreview draft={draft} />
        <BannerFields idPrefix="new-banner" draft={draft} setDraft={setDraft} touched={touched} />
        {add.isError ? <ErrorNotice inline title="Banner not added" message={errorMessage(add.error)} /> : null}
      </div>
    </Drawer>
  );
}

/**
 * One banner: its preview, a warning when its window is crowded, editing, and
 * taking it down.
 *
 * @param props - The banner (null when closed).
 * @returns The side panel.
 */
function BannerDrawer({
  banner,
  onClose,
}: {
  readonly banner: PromoBannerRecord | null;
  readonly onClose: () => void;
}): React.ReactElement {
  return (
    <Drawer
      open={banner !== null}
      size="wide"
      title={banner?.headline ?? 'Banner'}
      {...(banner ? { subtitle: `${kindLabel(banner.kind)} · ${statusLabel(banner.status)}` } : {})}
      onClose={onClose}
    >
      {/* Keyed so the form starts from the banner it is showing. */}
      {banner === null ? null : <BannerBody key={banner.id} banner={banner} onRemoved={onClose} />}
    </Drawer>
  );
}

function BannerBody({
  banner,
  onRemoved,
}: {
  readonly banner: PromoBannerRecord;
  readonly onRemoved: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const [draft, setDraft] = useState<BannerDraft>(() => draftOf(banner));
  const [touched, setTouched] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const save = useMutation({ mutationFn: () => updatePromoBanner(banner.id, inputOf(draft)), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: () => removePromoBanner(banner.id),
    onSuccess: async () => {
      await refresh();
      onRemoved();
    },
  });

  const saved = draftOf(banner);
  const changed = JSON.stringify(inputOf(draft)) !== JSON.stringify(inputOf(saved));
  const canSave = Object.keys(draftProblems(draft)).length === 0;

  return (
    <div className="space-y-6">
      <BannerPreview draft={draft} />

      {isCrowded(banner) ? (
        <div data-guide="banner-crowded"><ErrorNotice
          inline
          tone="warning"
          message={`${banner.overlapping} other banners could share a portal with this one while it runs. A portal shows ${MAX_LIVE_BANNERS_PER_PORTAL} at a time, so some may not appear. Consider narrowing the audience or the dates.`}
        /></div>
      ) : null}

      <BannerFields idPrefix="banner" draft={draft} setDraft={setDraft} touched={touched} />

      <div className="flex items-center justify-between gap-3">
        <p className="text-portal-micro text-portal-muted">
          {banner.createdBy ? `Added by ${banner.createdBy} on ` : 'Added on '}
          {readableDay(banner.createdAt.slice(0, 10))}
        </p>
        <Button
          disabled={!changed || save.isPending}
          onClick={() => {
            setTouched(true);
            if (canSave) save.mutate();
          }}
        >
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
      {save.isError ? <ErrorNotice inline title="Changes not saved" message={errorMessage(save.error)} /> : null}
      {save.isSuccess && !changed ? <p className="text-portal-micro text-portal-muted" role="status">Saved.</p> : null}

      <div className="border-t border-portal-line pt-5">
        <Button variant="secondary" onClick={() => setConfirmRemove(true)}>
          Remove banner
        </Button>
        <p className="mt-1 text-portal-micro text-portal-muted">It comes down from every portal at once.</p>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove this banner?"
        message="It comes down from every portal it shows on and leaves this list."
        confirmLabel="Remove"
        tone="danger"
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmRemove(false)}
      />
      {remove.isError ? <ErrorNotice inline title="Banner not removed" message={errorMessage(remove.error)} /> : null}
    </div>
  );
}
